<template>
  <div class="grid-container">
    <div class="c-bill-estimate">
      <div class="grid-x">
        <div class="cell medium-4">
          <img class="c-bill-estimate__image c-bill-estimate__image--01" v-bind:src="billEstimateImage1" alt="">
          <img class="c-bill-estimate__image c-bill-estimate__image--02" v-bind:src="billEstimateImage2" alt="">
        </div>
        <div class="cell medium-7">
          <h1 class="c-bill-estimate__title">{{billEstimateTitle}}</h1>

          <div class="c-bill-estimate__step">
            <h2 class="c-bill-estimate__step-title">Step 1</h2>
            <div class="c-bill-estimate__step-description" v-html="billEstimateStep1">
            </div>
          </div>

          <div class="c-bill-estimate__step">
            <h2 class="c-bill-estimate__step-title">Step 2</h2>
            <div class="c-bill-estimate__step-description" v-html="billEstimateStep2">
            </div>
          </div>

          <div class="c-bill-estimate__calculator">
            <div class="c-bill-estimate__calculator-item">
              <label class="c-bill-estimate__label" for="water-use">Please enter your estimated annual water use in cubic meters (m&sup3;)</label>
              <div class="c-bill-estimate__input">
                <input 
                  class="c-bill-estimate__input-field" 
                  v-model="waterUse" 
                  type="number" 
                  step="0.01"
                  name="water-use" 
                  id="water-use"
                  min="0"
                  max="400"
                  aria-describedby="form-unit-01" />
                <span class="c-bill-estimate__input-unit" id="form-unit-01">m&sup3;</span>
              </div>
              <span class="c-bill-estimate__error" v-show="waterUseError">{{waterUseError}}</span>
            </div>
            <div class="c-bill-estimate__calculator-item">
              <label class="c-bill-estimate__label" for="current-bill">Please enter your annual bill</label>
              <div class="c-bill-estimate__input">
                <span class="c-bill-estimate__input-unit" id="form-unit-02">&pound;</span>
                <input 
                  class="c-bill-estimate__input-field" 
                  type="number" 
                  step="0.01"
                  v-model="currentBill" 
                  name="current-bill" 
                  id="current-bill"
                  min="0"
                  aria-describedby="form-unit-02" />
              </div>
            </div>
            <span class="c-bill-estimate__error" v-show="currentBillError">{{currentBillError}}</span>
            <div class="c-bill-estimate__calculator-actions">
              <button 
              class="c-button c-button--brand" 
              type="button"
              v-on:click="calcWaterCharges()">Calculate charges</button>
            </div>

          </div>
          <div v-show="showResults">
          <table class="c-bill-estimate__charges">
            <caption class="c-bill-estimate__charges-title">Water charges</caption>
            <tr>
              <th class="c-bill-estimate__charges-item" scope="row">Water charges (estimated use x &pound;{{waterCharge}} per
                m&sup3;)</th>
              <td class="c-bill-estimate__charges-item c-bill-estimate__charges-item--data">&pound;{{estimatedWaterCharge}}</td>
            </tr>
            <tr>
              <th class="c-bill-estimate__charges-item" scope="row">Plus annual standing charge</th>
              <td class="c-bill-estimate__charges-item c-bill-estimate__charges-item--data">&pound;{{standingWaterCharge}}</td>
            </tr>
            <tr class="c-bill-estimate__charges-total">
              <th class="c-bill-estimate__charges-item" scope="row">Total</th>
              <td class="c-bill-estimate__charges-item c-bill-estimate__charges-item--data">&pound;{{totalWaterCharge}}</td>
            </tr>
          </table>
          <table class="c-bill-estimate__charges">
            <caption class="c-bill-estimate__charges-title">Sewage charges*</caption>
            <tr>
              <th class="c-bill-estimate__charges-item" scope="row">Your estimate of water returned to the sewer (95%
                of water use)</th>
              <td class="c-bill-estimate__charges-item c-bill-estimate__charges-item--data">{{estimatedReturnedWater}}m&sup3;</td>
            </tr>
            <tr>
              <th class="c-bill-estimate__charges-item" scope="row">Sewerage charge (estimated use x &pound;{{sewerageCharge}} per
                m&sup3;)</th>
              <td class="c-bill-estimate__charges-item c-bill-estimate__charges-item--data">&pound;{{estimatedSewerageCharge}}</td>
            </tr>
            <tr>
              <th class="c-bill-estimate__charges-item" scope="row">Plus annual standing charge</th>
              <td class="c-bill-estimate__charges-item c-bill-estimate__charges-item--data">&pound;{{standingSewageCharge}}</td>
            </tr>
            <tr class="c-bill-estimate__charges-total">
              <th class="c-bill-estimate__charges-item" scope="row">Total</th>
              <td class="c-bill-estimate__charges-item c-bill-estimate__charges-item--data">&pound;{{totalSewerageCharge}}</td>
            </tr>
          </table>

          <table class="c-bill-estimate__results">
            <tr>
              <th class="c-bill-estimate__results-item">Your total estimated metered bill</th>
              <td class="c-bill-estimate__results-item c-bill-estimate__results-item--data">&pound;{{totalEstimatedMeteredBill}}</td>
            </tr>
            <tr v-show="showSavings">
              <th class="c-bill-estimate__results-savings">Switching to a meter will save you</th>
              <td class="c-bill-estimate__results-savings c-bill-estimate__results-savings--data">&pound;{{totalSavings}}</td>
            </tr>
          </table>

          <div class="c-bill-estimate__footnote">
            <p>* If you receive sewerage services from another water company, please use the sewerage charges from that
              company.
            </p>

            <p>We've included the standing charges which cover the cost of maintaining the meter, reading it and
              producing your bill. The sewerage standing charge also covers the cost of surface water and highway
              drainage.
            </p>
          </div>
          </div>
        </div>
      </div> 
    </div>
  </div>
</template>

<script>
import "babel-polyfill";
import Vue from "NodeModules/vue/dist/vue.min.js";

export default {
  name: "MeteredBill",
  props: ["waterCharge","standingWaterCharge", "standingSewageCharge", "sewerageCharge", "billEstimateTitle", "billEstimateStep1", "billEstimateStep2", "billEstimateImage1", "billEstimateImage2"],
  data() {
    return {
      showResults: false,
      waterUse: 0,
      waterUseError: '',
      currentBill: 0,
      currentBillError: '',
      waterChargePence: this.waterCharge * 100,
      standingWaterChargePence: this.standingWaterCharge * 100,
      standingSewageChargePence: this.standingSewageCharge * 100,
      sewerageChargePence: this.sewerageCharge * 100,
      estimatedWaterCharge: 0,
      totalWaterCharge: 0,
      estimatedSewerageCharge: 0,
      totalSewerageCharge: 0,
      totalEstimatedMeteredBill: 0
    }
  },
  computed: {
    estimatedReturnedWater: function() {
      return (this.waterUse * 0.95).toFixed(2);
    },
    showSavings: function() {      
      return  this.currentBill !== "" && parseFloat(this.currentBill) !== 0;
    },
    totalSavings: function() {
      if (this.currentBill > 0){
        return (parseFloat(this.currentBill) - parseFloat(this.totalEstimatedMeteredBill)).toFixed(2);
      }
    }
  },
  methods: {
    calcWaterCharges() {
      let fieldsValid = true;

      if (this.waterUse < 1 || this.waterUse > 400) {
        fieldsValid = false;
        this.waterUseError = 'Please enter an annual usage value between 1 and 400 m³. Typical usage would usually fall between 45 and 265 cubic meters per year.';
      } else {
        this.waterUseError = '';
      }

      if (this.currentBill < 0 || this.currentBill > 2000) {
        fieldsValid = false;
        this.currentBillError = 'You must enter a bill between £0 and £2000';
      } else {
        this.currentBillError = '';
      }

      if (fieldsValid) {
        this.showResults = true;
        this.estimatedWaterCharge = (parseFloat(this.waterUse) * parseFloat(this.waterCharge)).toFixed(2);
        this.totalWaterCharge = (((this.estimatedWaterCharge * 100) + this.standingWaterChargePence) / 100).toFixed(2);
        this.estimatedSewerageCharge = ((this.estimatedReturnedWater * this.sewerageChargePence) / 100).toFixed(2);
        this.totalSewerageCharge = (((this.estimatedSewerageCharge * 100) + this.standingSewageChargePence) / 100).toFixed(2);
        this.totalEstimatedMeteredBill = (parseFloat(this.totalWaterCharge) + parseFloat(this.totalSewerageCharge)).toFixed(2);
      } else {
        this.showResults = false;
      }
    }
  },
}
</script>


// WEBPACK FOOTER //
// src/components/components/consumer/bill-estimate/MeteredBill.vue